import type { Transition, Variants } from 'framer-motion';
import type { CSSProperties } from 'react';
import styles from './ItemView.module.css';
import type { IHamburgerItemViewProperties } from './interface';

const ANIMATION_DURATION_SUBLIST_SHOW = 0.6;
const ANIMATION_DURATION_SUBLIST_HIDE = 0.4;
const MIN_WIDTH_PER_SUBITEM = 42; /* Vw units */
const MAX_ONSCREEN_SUBITEMS = 2;

export enum StyleElement {
    Container = 'Container',
    BackgroundImage = 'BackgroundImage',
    ContainerBottom = 'ContainerBottom',
    ContainerTitle = 'ContainerTitle',
    TitleText = 'TitleText',
    SubItemContainer = 'SubItemContainer',
    SubItemCol = 'SubItemCol',
}

export enum SubListState {
    Collapsed = 'Collapsed',
    Expanded = 'Expanded',
}

const DEFAULTS: Record<StyleElement, CSSProperties> = {
    [StyleElement.Container]: {},
    [StyleElement.BackgroundImage]: {},
    [StyleElement.ContainerBottom]: {},
    [StyleElement.ContainerTitle]: {},
    [StyleElement.TitleText]: {},
    [StyleElement.SubItemContainer]: {},
    [StyleElement.SubItemCol]: {},
};

const RUNTIME: Record<StyleElement, Record<SubListState, CSSProperties>> = {
    [StyleElement.Container]: {
        [SubListState.Collapsed]: {
            height: '26vh',
        },
        [SubListState.Expanded]: {
            height: '37vh',
        },
    },
    [StyleElement.BackgroundImage]: {
        [SubListState.Collapsed]: {},
        [SubListState.Expanded]: {},
    },
    [StyleElement.ContainerBottom]: {
        [SubListState.Collapsed]: {
            height: '7vh',
        },
        [SubListState.Expanded]: {
            height: '28vh',
        },
    },
    [StyleElement.ContainerTitle]: {
        [SubListState.Collapsed]: {
            height: '7vh',
        },
        [SubListState.Expanded]: {
            height: '6vh',
        },
    },
    [StyleElement.TitleText]: { [SubListState.Collapsed]: {}, [SubListState.Expanded]: {} },
    [StyleElement.SubItemContainer]: {
        [SubListState.Collapsed]: {
            height: '0vh',
        },
        [SubListState.Expanded]: {
            height: '22vh',
        },
    },
    [StyleElement.SubItemCol]: { [SubListState.Collapsed]: {}, [SubListState.Expanded]: {} },
};

const PROPS_APPLICATOR: Record<
    StyleElement,
    (properties: IHamburgerItemViewProperties) => CSSProperties
> = {
    [StyleElement.Container]: (properties: IHamburgerItemViewProperties) => ({
        ...RUNTIME[StyleElement.Container][
            properties.isShowingSubItems ? SubListState.Expanded : SubListState.Collapsed
        ],
        backgroundColor: properties.backgroundColor,
    }),
    [StyleElement.BackgroundImage]: (properties: IHamburgerItemViewProperties) => ({}),
    [StyleElement.ContainerBottom]: (properties: IHamburgerItemViewProperties) => ({
        ...RUNTIME[StyleElement.ContainerBottom][
            properties.isShowingSubItems ? SubListState.Expanded : SubListState.Collapsed
        ],
        backgroundColor: properties.titleBackgroundColor,
        overflowX: properties.isShowingSubItems ? 'scroll' : 'hidden',
        overflowY: properties.isShowingSubItems ? 'scroll' : 'hidden',
    }),
    [StyleElement.ContainerTitle]: (properties: IHamburgerItemViewProperties) => ({
        ...RUNTIME[StyleElement.ContainerTitle][
            properties.isShowingSubItems ? SubListState.Expanded : SubListState.Collapsed
        ],
    }),
    [StyleElement.TitleText]: (properties: IHamburgerItemViewProperties) => ({
        ...RUNTIME[StyleElement.TitleText][
            properties.isShowingSubItems ? SubListState.Expanded : SubListState.Collapsed
        ],
        color: properties.title.color,
        fontSize: properties.title.fontSize,
        lineHeight: properties.title.fontSize,
        fontFamily: properties.title.fontFamily,
        textAlign: properties.title.fontAlignment,
        fontWeight: properties.title.fontWeight,
        opacity: properties.title.visible ? 1 : 0,
    }),
    [StyleElement.SubItemContainer]: (properties: IHamburgerItemViewProperties) => ({
        ...RUNTIME[StyleElement.SubItemContainer][
            properties.isShowingSubItems ? SubListState.Expanded : SubListState.Collapsed
        ],
        opacity: properties.isShowingSubItems ? 1 : 0,
        /* Set the sub-item container width equal to the scrollable size of sub-items */
        width:
            properties.subItems.length <= MAX_ONSCREEN_SUBITEMS
                ? '100%'
                : `${Math.ceil(MIN_WIDTH_PER_SUBITEM * properties.subItems.length)}vw`,
    }),
    [StyleElement.SubItemCol]: (properties: IHamburgerItemViewProperties) => ({
        ...RUNTIME[StyleElement.SubItemCol][
            properties.isShowingSubItems ? SubListState.Expanded : SubListState.Collapsed
        ],
        /* If we have more than 2 items, show part of item #3, indicating that we can swipe left/right to show */
        width:
            properties.subItems.length <= MAX_ONSCREEN_SUBITEMS
                ? `${Math.round(100 / MAX_ONSCREEN_SUBITEMS)}%`
                : `${MIN_WIDTH_PER_SUBITEM}vw`,
    }),
};

export function animationVariantsForElement(element: StyleElement): Variants {
    /* We want to delay the animation of the subitems appearing, otherwise we will show resizing animations of the subitem-images */
    const isAnimationDelayed = (element_: StyleElement) =>
        element_ === StyleElement.SubItemContainer || element_ === StyleElement.SubItemCol;

    const animHide: Transition = {
        type: 'spring',
        delay: isAnimationDelayed(element) ? 0.2 : 0,
        bounce: 0.1,
        duration: ANIMATION_DURATION_SUBLIST_HIDE,
    };

    const animShow: Transition = {
        type: 'spring',
        delay: isAnimationDelayed(element) ? 0 : 0.3,
        bounce: 0.1,
        duration: ANIMATION_DURATION_SUBLIST_SHOW,
    };

    const variants = {
        [SubListState.Collapsed]: {
            ...RUNTIME[element][SubListState.Collapsed],
            transition: animHide,
        },
        [SubListState.Expanded]: {
            ...RUNTIME[element][SubListState.Expanded],
            transition: animShow,
        },
    };

    /* Cannot Pass CSSProperties to Variants due to 'Omit' in CSSPropertiesWithoutTransitionOrSingleTransforms */
    return variants as Variants;
}

export function animationStateForElement(
    element: StyleElement,
    properties: IHamburgerItemViewProperties,
): string {
    return properties.isShowingSubItems ?? false ? SubListState.Expanded : SubListState.Collapsed;
}

export function classNameForElement(
    element: StyleElement,
    model: IHamburgerItemViewProperties,
): string {
    const name = element.toString();
    return styles[name] ?? '';
}

export function styleForElement(
    element: StyleElement,
    properties: IHamburgerItemViewProperties,
): CSSProperties {
    const style = { ...DEFAULTS[element], ...PROPS_APPLICATOR[element](properties) };

    return style;
}
